import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { toast } from "sonner";
import { ChevronLeft, Star, ThumbsUp, Bookmark, BookmarkCheck, Send, ShieldCheck, Sparkles, MapPin, CheckCircle2 } from "lucide-react";

function Stars({ value, onChange }) {
  return (
    <div className="flex gap-1" data-testid="wali-stars">
      {[1, 2, 3, 4, 5].map((n) => (
        <button key={n} onClick={() => onChange(n)} data-testid={`star-${n}`} className="p-0.5">
          <Star className={`h-5 w-5 ${n <= value ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} />
        </button>
      ))}
    </div>
  );
}

function CandidateCard({ memberId, p, savedIds, onChanged }) {
  const [note, setNote] = useState(p.myNote || "");
  const [rating, setRating] = useState(p.myRating || 0);
  const [busy, setBusy] = useState(false);
  const saved = savedIds.has(p.id);
  const photo = p.photoBase64 || (p.photos && p.photos[0]);
  const loc = [p.city, p.country].filter(Boolean).join(", ");

  const call = async (fn, msg) => { setBusy(true); try { await fn(); if (msg) toast.success(msg); await onChanged(); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setBusy(false); } };
  const saveNote = () => call(() => api.post("/assist/note", { memberId, targetId: p.id, note, rating }), "Saved for the family");
  const recommend = () => call(() => api.post("/assist/recommend", { memberId, targetId: p.id, note: note || undefined }), "Recommended to them");
  const toggleSave = () => call(() => api.post(saved ? "/assist/unsave" : "/assist/save", { memberId, targetId: p.id }), saved ? "Removed from saved" : "Saved to review");
  const propose = () => call(() => api.post("/assist/propose-interest", { memberId, targetId: p.id }), "Interest proposed — they'll confirm");

  return (
    <div className="rounded-2xl border border-emerald-900/10 bg-white p-5" data-testid={`wali-candidate-${p.id}`}>
      <div className="flex gap-4">
        <Link to={`/profiles/${p.id}`} className="shrink-0">
          <Avatar className="h-20 w-20 rounded-2xl border border-amber-500/30">
            {photo && <AvatarImage src={photo} alt="" className="object-cover" />}
            <AvatarFallback className="rounded-2xl bg-emerald-800 text-amber-200">{(p.displayName || "?")[0]}</AvatarFallback>
          </Avatar>
        </Link>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <Link to={`/profiles/${p.id}`} className="font-serif text-lg font-bold text-emerald-900 hover:underline">{p.displayName || p.firstName}{p.age ? `, ${p.age}` : ""}</Link>
            {p.verified && <CheckCircle2 className="h-4 w-4 text-emerald-600" title="Verified" />}
            {typeof p.matchScore === "number" && <span className="rounded-full bg-mint px-2 py-0.5 text-xs font-semibold text-emerald-800">{p.matchScore}% match</span>}
          </div>
          {loc && <p className="mt-0.5 flex items-center gap-1 text-sm text-slate-500"><MapPin className="h-3.5 w-3.5" /> {loc}</p>}
          {p.profession && <p className="text-sm text-slate-600">{p.profession}</p>}
          <div className="mt-1 flex flex-wrap gap-1.5 text-xs text-emerald-800/80">
            {[p.sect, p.maritalStatus, p.religiosity].filter(Boolean).map((t, i) => <span key={i} className="rounded-full bg-ivory px-2 py-0.5 ring-1 ring-emerald-900/10">{t}</span>)}
          </div>
          {(p.recommended || p.proposed) && (
            <div className="mt-2 flex flex-wrap gap-2">
              {p.recommended && <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-semibold text-emerald-800"><ThumbsUp className="h-3 w-3" /> Recommended</span>}
              {p.proposed && <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-800"><Send className="h-3 w-3" /> Interest proposed</span>}
            </div>
          )}
        </div>
      </div>

      {p.matchReasons?.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {p.matchReasons.slice(0, 4).map((r, i) => <span key={i} className="inline-flex items-center gap-1 rounded-full bg-mint/60 px-2.5 py-1 text-xs text-emerald-800"><Sparkles className="h-3 w-3" /> {r}</span>)}
        </div>
      )}

      <div className="mt-4 rounded-xl bg-ivory p-3">
        <div className="flex items-center justify-between">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Your rating</p>
          <Stars value={rating} onChange={setRating} />
        </div>
        <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Private note for the family about this match…" className="mt-2 bg-white text-sm" data-testid={`wali-note-${p.id}`} />
      </div>

      <div className="mt-3 flex flex-wrap gap-2">
        <Button size="sm" disabled={busy} onClick={saveNote} data-testid={`wali-savenote-${p.id}`} className="rounded-full bg-emerald-800 text-white hover:bg-emerald-900"><Star className="mr-1 h-3.5 w-3.5" /> Save note</Button>
        <Button size="sm" disabled={busy} variant="outline" onClick={recommend} data-testid={`wali-recommend-${p.id}`} className="rounded-full border-emerald-800/30 text-emerald-900 hover:bg-mint"><ThumbsUp className="mr-1 h-3.5 w-3.5" /> Recommend</Button>
        <Button size="sm" disabled={busy} variant="outline" onClick={toggleSave} data-testid={`wali-save-${p.id}`} className="rounded-full border-emerald-800/30 text-emerald-900 hover:bg-mint">{saved ? <><BookmarkCheck className="mr-1 h-3.5 w-3.5" /> Saved</> : <><Bookmark className="mr-1 h-3.5 w-3.5" /> Save</>}</Button>
        <Button size="sm" disabled={busy || p.proposed} variant="outline" onClick={propose} data-testid={`wali-propose-${p.id}`} className="rounded-full border-amber-500/40 text-amber-700 hover:bg-amber-50"><Send className="mr-1 h-3.5 w-3.5" /> Propose interest</Button>
      </div>
    </div>
  );
}

export default function WaliReview() {
  const { memberId } = useParams();
  const [data, setData] = useState(null);
  const [savedIds, setSavedIds] = useState(new Set());
  const [tab, setTab] = useState("all");

  const load = useCallback(async () => {
    try {
      const [m, s] = await Promise.all([
        api.get(`/assist/member/${memberId}/matches`),
        api.get("/assist/saved", { params: { memberId } }),
      ]);
      setData(m.data);
      setSavedIds(new Set((s.data.profiles || []).map((p) => p.id)));
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); setData({ profiles: [], member: null }); }
  }, [memberId]);
  useEffect(() => { load(); }, [load]);

  const member = data?.member;
  const profiles = data?.profiles || [];
  const shown = tab === "saved" ? profiles.filter((p) => savedIds.has(p.id)) : tab === "recommended" ? profiles.filter((p) => p.recommended) : profiles;

  return (
    <div className="mx-auto max-w-3xl" data-testid="wali-review-page">
      <Link to="/assisted" data-testid="wali-back" className="mb-4 inline-flex items-center gap-1 text-sm font-medium text-emerald-700 hover:underline"><ChevronLeft className="h-4 w-4" /> Back to Assisted Matchmaking</Link>

      {data === null ? <Skeleton className="h-24 rounded-3xl" /> : (
        <>
          <div className="rounded-3xl bg-gradient-to-br from-emerald-800 to-emerald-950 p-6 text-white">
            <p className="text-xs font-semibold uppercase tracking-wide text-amber-200">Reviewing matches for</p>
            <h1 className="mt-1 font-serif text-2xl font-bold sm:text-3xl">{member?.displayName || member?.firstName || "Your member"}</h1>
            <p className="mt-1 text-sm text-emerald-100/90">Add private notes, rate matches, recommend profiles or propose an interest on their behalf.</p>
          </div>

          <div className="mt-4 flex items-start gap-2.5 rounded-2xl bg-mint/40 p-3 text-xs text-emerald-900/80" data-testid="wali-privacy-note">
            <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-700" />
            <p>You can review and advise, but you can never message anyone or see contact details — those stay private to {member?.firstName || "them"}.</p>
          </div>

          <div className="mt-4 flex gap-2">
            {[["all", `All (${profiles.length})`], ["saved", `Saved (${savedIds.size})`], ["recommended", "Recommended"]].map(([k, l]) => (
              <button key={k} onClick={() => setTab(k)} data-testid={`wali-tab-${k}`} className={`rounded-full px-4 py-2 text-sm font-medium ${tab === k ? "bg-emerald-800 text-white" : "bg-white text-emerald-900/70 ring-1 ring-emerald-900/10 hover:bg-mint"}`}>{l}</button>
            ))}
          </div>

          <div className="mt-4 space-y-4">
            {shown.length === 0 ? <p className="py-10 text-center text-slate-500" data-testid="wali-empty">Nothing here yet.</p>
              : shown.map((p) => <CandidateCard key={p.id} memberId={memberId} p={p} savedIds={savedIds} onChanged={load} />)}
          </div>
        </>
      )}
    </div>
  );
}
